import React, { useState, useEffect } from 'react'
import {
    Button,
    TextField,
    Popover,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
} from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { Container } from '../../Container'
import {
    ConversationProblem,
    ProblemCategory,
    PROBLEM_CATEGORY_OPTIONS,
} from './analysisTypes'

interface Props {
    anchorEl: HTMLElement | null
    onClose: () => void
    conversationId: number
    existingProblem: ConversationProblem | null
    onCreate: (data: {
        conversation: number
        category: ProblemCategory
        notes?: string
    }) => void
    onUpdate: (data: {
        id: number
        category: ProblemCategory
        notes?: string
    }) => void
    onDelete: (id: number) => void
    saving?: boolean
}

export const ProblemFlagPopover = ({
    anchorEl,
    onClose,
    conversationId,
    existingProblem,
    onCreate,
    onUpdate,
    onDelete,
    saving,
}: Props) => {
    const theme = useTheme()
    const isEditing = existingProblem !== null

    const [category, setCategory] = useState<ProblemCategory | ''>('')
    const [notes, setNotes] = useState('')

    useEffect(() => {
        if (anchorEl) {
            setCategory(existingProblem?.category ?? '')
            setNotes(existingProblem?.notes ?? '')
        }
    }, [anchorEl, existingProblem])

    const handleSave = () => {
        if (!category) return
        if (isEditing) {
            onUpdate({
                id: existingProblem!.id,
                category,
                notes: notes || undefined,
            })
        } else {
            onCreate({
                conversation: conversationId,
                category,
                notes: notes || undefined,
            })
        }
        onClose()
    }

    const handleDelete = () => {
        if (existingProblem) {
            onDelete(existingProblem.id)
            onClose()
        }
    }

    return (
        <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
            }}
        >
            <Container
                style={{
                    padding: theme.spacing(2),
                    minWidth: 320,
                    flexDirection: 'column',
                    gap: theme.spacing(2),
                }}
            >
                <Typography variant="h6">
                    {isEditing ? 'Edit Problem' : 'Flag Problem'}
                </Typography>

                <FormControl variant="outlined" fullWidth size="small">
                    <InputLabel id="problem-category-label">
                        Category
                    </InputLabel>
                    <Select
                        labelId="problem-category-label"
                        value={category}
                        onChange={(e) =>
                            setCategory(e.target.value as ProblemCategory)
                        }
                        label="Category"
                    >
                        {PROBLEM_CATEGORY_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    label="Notes (optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    size="small"
                />

                <Container
                    style={{
                        flexDirection: 'row',
                        justifyContent: isEditing
                            ? 'space-between'
                            : 'flex-end',
                        gap: theme.spacing(1),
                    }}
                >
                    {isEditing && (
                        <Button
                            size="small"
                            onClick={handleDelete}
                            disabled={saving}
                            style={{
                                color: theme.palette.error.main,
                                textTransform: 'none',
                            }}
                        >
                            Remove Flag
                        </Button>
                    )}
                    <Container
                        style={{
                            flexDirection: 'row',
                            gap: theme.spacing(1),
                        }}
                    >
                        <Button
                            size="small"
                            onClick={onClose}
                            style={{ textTransform: 'none' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={handleSave}
                            disabled={!category || saving}
                            style={{ textTransform: 'none' }}
                        >
                            Save
                        </Button>
                    </Container>
                </Container>
            </Container>
        </Popover>
    )
}
